/**
 * GRIPS-INTERACTION PR-2 (Ctrl→Enter/Space correction, §8.2) — command-line
 * echo hook for the armed hot-grip modal prompt ("** STRETCH **" / "** MOVE
 * **" / …). Extracted out of `Scene3DStage.tsx` to keep that file under the
 * repo's file-size gate (RP4 split pattern — small effects live in a
 * `./scene/*` hook, same as `usePolarDraftAidEffects`).
 *
 * Two DISTINCT signals feed the echo, because they can't share one:
 *
 *  1. ARM + Enter/Space CYCLE — subscribes to `hotGripStore` directly
 *     (`armedGrip` null↔non-null = arm/disarm; `armedEditMode` = the cycled
 *     mode) and, on every genuine arm/cycle transition, CLEARS the echo
 *     (`""`) rather than echoing the mode text. fix/grip-armed-prompt-dedup —
 *     `GripArmedPrompt.tsx` (N4 GRIPS-ARMED-STRETCH-I18N) now owns the
 *     while-armed status surface (its banner line reuses this SAME
 *     `grip.mode.<mode>` key, richer content overall — mode banner + point
 *     prompt), and App.tsx additionally gates this echo strip's render off
 *     entirely while a grip is armed. Setting the mode text HERE too used to
 *     leave STALE non-empty text sitting in `gripModeEchoText` for the
 *     entire armed session — harmless while the render gate suppressed it,
 *     but on an ABORT-style disarm (Escape/blur/outside-click/deselect-all/
 *     entity-leaves-selection) the store's `armedGrip->null` transition flips
 *     the render gate open SYNCHRONOUSLY (same render as `GripArmedPrompt`
 *     unmounting), while this hook's own clear-to-"" (see the `else` branch
 *     below) runs one render LATER as a passive effect — a window where the
 *     STALE mode text could render ALONE for one frame. Clearing to `""` on
 *     arm/cycle instead keeps `gripModeEchoText` PROVABLY empty for the
 *     entire armed session (arm through commit-or-abort), so there is never
 *     anything stale to flash regardless of effect-flush timing. This also
 *     synchronously wipes any leftover PREVIOUS commit-summary text the
 *     moment a NEW grip arms (same render as the new `GripArmedPrompt`
 *     appearing), so a stale summary can never bleed into the next armed
 *     session either.
 *  2. COMMIT — the store transition alone CANNOT tell a commit from an abort
 *     (both clear `armedGrip` identically), so the commit echo is instead
 *     driven by `StageGripDrag.ts`'s `onGripArmedCommit` callback, which
 *     fires ONLY from `onArmedCommit` (i.e. ONLY a genuine click-arm commit —
 *     never a plain press-drag, which never installs that handler at all).
 *     This hook returns a STABLE relay for that callback so the caller can
 *     wire it straight into `buildGripStartHandler` without rebuilding the
 *     memoised grip bundle on every `onGripModeEcho` prop-identity churn
 *     (the same Codex P1 #317 staleness/rebuild-cost class documented on the
 *     other grip callbacks in `Scene3DStage.tsx`). This is the ONE remaining
 *     legitimate use of the echo strip's text — `GripArmedPrompt` has
 *     already unmounted (armed grip cleared) by the time it shows, so there
 *     is no overlap with it.
 */

import { useCallback, useEffect, useRef, useSyncExternalStore } from "react";

import {
  getArmedEditModeSnapshot,
  getArmedGripSnapshot,
  subscribeHotGrips,
} from "../grips/hotGripStore";
import type { GripEditMode } from "../grips/gripInteractionConstants";
import { t } from "../../i18n";

/**
 * @param onGripModeEcho the `Scene3DStageProps.onGripModeEcho` prop (App.tsx
 *   wires this to the existing `appendCommandFeed`).
 * @returns a STABLE `(mode) => void` relay — wire it as
 *   `buildGripStartHandler`'s `onGripArmedCommit` arg for the commit echo.
 */
export function useGripModeEcho(
  onGripModeEcho: ((text: string) => void) | undefined,
): (mode: GripEditMode) => void {
  // ARM + CYCLE — direct store subscription, transition-gated so it fires
  // once per genuine arm/cycle, not once per render.
  const armedGripForEcho = useSyncExternalStore(
    subscribeHotGrips,
    getArmedGripSnapshot,
    getArmedGripSnapshot,
  );
  const armedEditModeForEcho = useSyncExternalStore(
    subscribeHotGrips,
    getArmedEditModeSnapshot,
    getArmedEditModeSnapshot,
  );
  const prevRef = useRef<{ armed: boolean; mode: GripEditMode | null }>({
    armed: false,
    mode: null,
  });
  // GRIPS-INTERACTION PR-2 fix — now that the echo is a LIVE status div (not
  // a scrolling transcript), a stale mode label lingering after an Escape/
  // abort disarm is a real cosmetic gap, so this flag distinguishes a
  // commit-driven disarm (leave the just-set `summary.<mode>` text alone)
  // from an abort-driven one (clear it). Set synchronously by the returned
  // commit relay below, which `StageGripDrag.ts`'s `onArmedCommit` ALWAYS
  // calls before `finish("commit")` clears the store (see its line ~616
  // commit-relay call vs. line ~715 disarm) — so by the time THIS effect
  // observes the armed→null transition, the flag is already `true` for a
  // commit and still `false` for every other disarm path (Escape, blur,
  // outside-canvas click, deselect-all, selection-drop).
  const commitJustFiredRef = useRef(false);
  useEffect(() => {
    const prev = prevRef.current;
    const armedNow = armedGripForEcho !== null;
    if (armedNow && (!prev.armed || prev.mode !== armedEditModeForEcho)) {
      // fix/grip-armed-prompt-dedup — CLEAR, don't echo the mode text:
      // `GripArmedPrompt` now owns the while-armed status surface (see the
      // header doc comment above for the full stale-flash rationale this
      // closes). Still runs on every arm/cycle transition (not just the
      // first arm) so a stale PREVIOUS commit-summary is wiped the instant a
      // NEW grip arms, synchronously with `GripArmedPrompt` reappearing.
      onGripModeEcho?.("");
    } else if (!armedNow && prev.armed) {
      if (commitJustFiredRef.current) {
        commitJustFiredRef.current = false;
      } else {
        onGripModeEcho?.("");
      }
    }
    prevRef.current = {
      armed: armedNow,
      mode: armedNow ? armedEditModeForEcho : null,
    };
  }, [armedGripForEcho, armedEditModeForEcho, onGripModeEcho]);

  // COMMIT — ref-backed relay so the returned function keeps a STABLE
  // identity regardless of `onGripModeEcho`'s own (possibly every-render)
  // identity churn.
  const onGripModeEchoRef = useRef(onGripModeEcho);
  onGripModeEchoRef.current = onGripModeEcho;
  return useCallback((mode: GripEditMode) => {
    commitJustFiredRef.current = true;
    onGripModeEchoRef.current?.(t(`summary.${mode}`));
  }, []);
}
